import { Link } from '@tanstack/react-router'
import { ArrowRightCircle } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'

import { InlineNoteMarkdown, NoteMarkdown } from '@/components/NoteMarkdown'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import type { ServiceStatus, StatusChangeLog } from '@/lib/schemas/db'

type ServiceTab = 'details' | 'logs' | 'invoice'

/**
 * The booked-in issue text, collapsed to its first line (markdown-rendered
 * inline) with a toggle to expand the full markdown. Multi-line issues used
 * to swallow the whole header, so the header shows one line by default.
 */
export function InitialIssue({ issue }: { issue: string }) {
  const [expanded, setExpanded] = useState(false)
  const lines = issue
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  const firstLine = lines[0] ?? ''
  const hasMore = lines.length > 1

  const toggleClass =
    'ml-1.5 cursor-pointer text-xs font-medium text-blue-700 hover:underline'

  if (expanded) {
    return (
      <div className="text-sm font-normal text-slate-600">
        <span>
          Issue:{' '}
          <button type="button" onClick={() => setExpanded(false)} className={toggleClass}>
            less
          </button>
        </span>
        <NoteMarkdown text={issue} />
      </div>
    )
  }

  return (
    <p className="text-sm font-normal text-slate-600">
      Issue: <InlineNoteMarkdown text={firstLine} />
      {hasMore ? (
        <button type="button" onClick={() => setExpanded(true)} className={toggleClass}>
          more…
        </button>
      ) : null}
    </p>
  )
}

interface ServicePageShellProps {
  serviceTypeLabel: string
  serviceId: string
  customerName: string
  customerId?: string
  customerPublicId?: string
  scheduledAtLabel?: string
  initialIssue?: string
  activeTab: ServiceTab
  basePath: '/callouts/$id' | '/sessions/$id' | '/jobs/$id'
  currentStatus?: ServiceStatus
  statusChanges?: StatusChangeLog[]
  getAuthorDisplayName?: (authorId: string) => string
  children: ReactNode
}

export function ServicePageShell({
  serviceTypeLabel,
  serviceId,
  customerName,
  customerId,
  customerPublicId,
  scheduledAtLabel,
  initialIssue,
  activeTab,
  basePath,
  currentStatus,
  statusChanges,
  getAuthorDisplayName,
  children,
}: ServicePageShellProps) {
  const tabs = [
    { key: 'details' as const, label: 'Details', to: basePath },
    { key: 'logs' as const, label: 'Log Work', to: `${basePath}/logs` as const },
    { key: 'invoice' as const, label: 'Invoice Prep', to: `${basePath}/invoice` as const },
  ]

  const isCallout = basePath.startsWith('/callouts')
  const isJobs = basePath.startsWith('/jobs')
  const listTo = isCallout ? '/callouts' : isJobs ? '/jobs' : '/sessions'
  const listLabel = isCallout ? 'Callouts' : isJobs ? 'Jobs' : 'Sessions'
  const serviceTypePillClass = isCallout
    ? 'rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700'
    : isJobs
      ? 'rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700'
      : 'rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-semibold text-purple-700'

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto max-w-container space-y-4 px-4 pb-6 pt-0">
        <section className="relative left-1/2 right-1/2 -mx-[50vw] w-screen rounded-none border-y border-slate-200 bg-slate-100 pb-0 pt-3">
          <div className="mx-auto max-w-container px-4">
            <header className="flex flex-col gap-2">
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-800">
                  <Link to={listTo} className="text-blue-700 hover:underline">
                    {listLabel}
                  </Link>{' '}
                  / <span>{serviceId}</span>
                </p>
                {scheduledAtLabel ? (
                  <div className="text-lg font-medium text-slate-700">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xl font-semibold text-slate-900">{scheduledAtLabel}</p>
                      {currentStatus ? (
                        <StatusHistoryPill
                          currentStatus={currentStatus}
                          statusChanges={statusChanges}
                          getAuthorDisplayName={getAuthorDisplayName}
                        />
                      ) : null}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-base font-medium text-slate-700">{customerName || serviceTypeLabel}</p>
                      {customerId ? (
                        <Link
                          to="/customers/$id"
                          params={{ id: customerPublicId ?? customerId }}
                          className="text-blue-700 hover:text-blue-800"
                          aria-label="Open customer"
                          title="Open customer"
                        >
                          <ArrowRightCircle className="h-4 w-4" />
                        </Link>
                      ) : null}
                    </div>
                    {initialIssue ? <InitialIssue issue={initialIssue} /> : null}
                    <span className={serviceTypePillClass}>{serviceTypeLabel}</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-base font-medium text-slate-700">{customerName || serviceTypeLabel}</p>
                      {customerId ? (
                        <Link
                          to="/customers/$id"
                          params={{ id: customerPublicId ?? customerId }}
                          className="text-blue-700 hover:text-blue-800"
                          aria-label="Open customer"
                          title="Open customer"
                        >
                          <ArrowRightCircle className="h-4 w-4" />
                        </Link>
                      ) : null}
                    </div>
                    <span className={serviceTypePillClass}>{serviceTypeLabel}</span>
                  </div>
                )}
              </div>
            </header>

            <nav aria-label="Service tabs" className="relative mt-3">
              <div className="pointer-events-none absolute bottom-0 left-1/2 right-1/2 -mx-[50vw] w-screen border-b border-slate-300" />
              <div className="relative z-10 flex flex-wrap items-end gap-1">
                {/* Invoice Prep is always visitable — what stays gated is the
                    "Mark as invoiced" action on it, which requires
                    ReadyToInvoice (see InvoiceServiceScreen.canMarkInvoiced). */}
                {tabs.map((tab) => {
                  const isActive = tab.key === activeTab
                  const tabClass = isActive
                    ? 'rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 -mb-px'
                    : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200'
                  const tabTo = tab.key === 'details' ? basePath : tab.to

                  return (
                    <Link
                      key={tab.key}
                      to={tabTo}
                      params={{ id: serviceId }}
                      className={tabClass}
                    >
                      {tab.label}
                    </Link>
                  )
                })}
              </div>
            </nav>
          </div>
        </section>

        {children}
      </main>
    </div>
  )
}
